import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Button, Modal as BootstrapModal, ModalFooter, ModalHeader } from 'reactstrap';
import { bind } from '../Utils/Bind';
import { Deferred } from '../Utils/Deferred';
import { UserAgent } from '../Utils/UserAgent';

export class ModalHandle {
    @bind
    @action
    public open() {
        this.isShowing = true;
        this.deferred = new Deferred<boolean>();
    }

    @bind
    public async confirm() {
        this.open();
        return this.deferred!.promise;
    }

    @bind
    @action
    public close() {
        if (!this.deferred) throw new Error('Modal must be open first');

        this.isShowing = false;
        this.deferred.resolve(true);
    }

    @bind
    @action
    public reject() {
        if (!this.deferred) throw new Error('Modal must be open first');

        this.isShowing = false;
        this.deferred.resolve(false);
    }

    @bind
    @action
    public resolve() {
        if (!this.deferred) throw new Error('Modal must be open first');

        this.isShowing = false;
        this.deferred.resolve(true);
    }

    @computed
    public get show() {
        return this.isShowing;
    }

    @observable
    public isShowing: boolean = false;

    @observable
    public deferred: Deferred<boolean> | undefined;
}
export interface IModalProps {
    onOpened?(): void;
    onClosed?(): void;
    bodyRef?(r: HTMLDivElement | null): HTMLDivElement | null;
    readonly modalHandle: ModalHandle;
    readonly title?: React.ReactNode;
    readonly titleClass?: string;
    readonly className?: string;
    readonly size?: string;
    readonly keyboard?: boolean;
    readonly backdrop?: boolean | 'static';
    readonly footer?: React.ReactNode;
    readonly footerClass?: string;
    readonly bodyClass?: string;
    readonly btnClass?: string;
}

@observer
export class Modal extends React.Component<IModalProps> {
    public render() {
        const BM = BootstrapModal as any;
        return (
            <BM
                size={this.props.size}
                isOpen={this.props.modalHandle.show}
                toggle={this.reject}
                className={UserAgent.iphone() ? this.props.className + ' safari-mobile' : this.props.className}
                onOpened={this.props.onOpened}
                onClosed={this.props.onClosed}
                keyboard={this.props.keyboard}
                backdrop={this.props.backdrop}
            >
                {this.props.title && (
                    <ModalHeader toggle={this.reject} className={this.props.titleClass + ' rounded-top'}>
                        {this.props.title}
                    </ModalHeader>
                )}

                <div className={`${this.props.bodyClass} modal-body`} ref={this.props.bodyRef}>
                    {this.props.children}
                </div>

                {this.props.footer && (
                    <ModalFooter className={this.props.footerClass + ' rounded-bottom'}>
                        {this.props.footer}
                    </ModalFooter>
                )}
                {!this.props.footer && (
                    <ModalFooter className={this.props.footerClass + ' rounded-bottom'}>
                        <Button className={this.props.btnClass} onClick={this.reject}>
                            Cancel
                        </Button>
                        <Button className={this.props.btnClass} color="primary" onClick={this.resolve}>
                            Ok
                        </Button>
                    </ModalFooter>
                )}
            </BM>
        );
    }

    @bind
    private reject() {
        this.props.modalHandle.reject();
    }

    @bind
    private resolve() {
        this.props.modalHandle.resolve();
    }
}

@observer
export class FullModal extends React.Component<IModalProps> {
    public render() {
        const BM = BootstrapModal as any;
        return (
            <BM
                size={this.props.size}
                isOpen={this.props.modalHandle.show}
                toggle={this.reject}
                className={this.props.className + ' m-0 rounded-0 full-modal h-100 '}
                onOpened={this.props.onOpened}
                onClosed={this.props.onClosed}
                fade={true}
            >
                {this.props.title && (
                    <ModalHeader toggle={this.reject} className={this.props.titleClass + ' rounded-0'}>
                        {this.props.title}
                    </ModalHeader>
                )}

                <div className={`${this.props.bodyClass} modal-body h-100`} ref={this.props.bodyRef}>
                    {this.props.children}
                </div>
            </BM>
        );
    }

    @bind
    private reject() {
        this.props.modalHandle.reject();
    }
}
